import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import authService from '../auth/AuthService';
import AuthUtil from './Utils/AuthUtil';
import { logout } from '../store/correcttrackSlice';

/**
 * Permanent banner shown while a super admin is acting as another user. Anything done in this
 * state is attributed to the impersonated user, so the reminder is deliberately hard to miss and
 * stays pinned above the rest of the layout.
 */
const ImpersonationBanner = (props) => {
    const { setSnackbarProperties } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [returning, setReturning] = useState(false);

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isImpersonating = useMemo(() => AuthUtil.isImpersonating(userToken), [userToken]);
    const impersonatedUsername = useMemo(() => AuthUtil.getUsername(userToken), [userToken]);
    const impersonatorUsername = useMemo(() => AuthUtil.getImpersonatorUsername(userToken), [userToken]);

    if (!isImpersonating) {
        return null;
    }

    const onReturnClick = async () => {
        setReturning(true);
        try {
            const response = await authService.stopImpersonating();
            if (response?.success) {
                // Full reload so nothing loaded as the impersonated user survives the switch back.
                window.location.href = '/';
                return;
            }

            setSnackbarProperties(response?.message ?? t('impersonation.returnFailed'), 'error');
        } catch (e) {
            // The session can no longer be unwound cleanly (expired or revoked); the safe fallback
            // is to drop it entirely and make the admin sign in again.
            dispatch(logout());
            window.location.href = '/';
            return;
        }
        setReturning(false);
    };

    return (
        <Alert
            severity="warning"
            variant="filled"
            square
            sx={{
                // Layout sticks the banner and the AppBar together as one unit; sticking again
                // here would pin this to the same offset as the AppBar and let it be covered.
                borderRadius: 0,
                alignItems: 'center',
            }}
            action={
                <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    disabled={returning}
                    onClick={onReturnClick}
                    startIcon={returning ? <CircularProgress color="inherit" size={16} /> : null}
                >
                    {t('impersonation.returnToMyAccount')}
                </Button>
            }
        >
            {t('impersonation.banner', { user: impersonatedUsername, admin: impersonatorUsername })}
        </Alert>
    );
};

ImpersonationBanner.propTypes = {
    setSnackbarProperties: PropTypes.func.isRequired,
};

export default ImpersonationBanner;